import React from 'react';
import { Link } from 'react-router-dom';
import { BUSINESS } from '../config';

const LAST_UPDATE = 'juillet 2026';

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-heading font-heading text-xl uppercase tracking-wider text-primary mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

export default function Cgv() {
  const L = BUSINESS.legal;
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-16" data-testid="cgv-page">
      <h1 className="font-heading font-heading text-3xl uppercase tracking-tight text-primary mb-2">
        Conditions générales de vente
      </h1>
      <p className="text-xs text-slate-500 mb-8">Dernière mise à jour : {LAST_UPDATE}</p>

      <div className="prose prose-slate max-w-none space-y-8 text-slate-700 text-sm leading-relaxed">

        <Section title="Article 1 — Identité du vendeur">
          <p>
            Le site <strong className="text-primary">{BUSINESS.url.replace('https://', '')}</strong>, exploité sous
            l'enseigne <strong className="text-primary">{BUSINESS.name}</strong>, est édité par :
          </p>
          <p>
            <strong className="text-primary">{L.raisonSociale}</strong> — {L.formeJuridique} au capital de {L.capital}<br />
            Siège social : {L.siege}<br />
            {L.rcs} — SIRET : {L.siret}<br />
            TVA intracommunautaire : {L.tva}<br />
            Boutique et retrait : {BUSINESS.adresse}<br />
            Téléphone : {BUSINESS.telephone} — <Link to="/contact" className="text-secondary hover:underline">formulaire de contact</Link>
          </p>
        </Section>

        <Section title="Article 2 — Champ d'application">
          <p>
            Les présentes conditions générales de vente (CGV) régissent les ventes conclues entre {BUSINESS.name} et
            ses clients. Les articles 3 à 14 s'appliquent aux <strong>consommateurs</strong> (particuliers). Les ventes
            aux <strong>professionnels</strong> sont précisées à l'article 15. Toute commande implique l'adhésion sans
            réserve aux présentes CGV.
          </p>
        </Section>

        <Section title="Article 3 — Produits et disponibilité">
          <p>
            Les produits proposés sont neufs ou reconditionnés. Un produit est soit <strong>en stock</strong>{' '}
            (disponible immédiatement : stock {BUSINESS.name} ou produit reconditionné en atelier), soit
            <strong> hors stock</strong> (commandé spécialement chez le fournisseur à la demande du client, dans un
            délai indiqué à titre estimatif). La commande porte sur un <strong>panier</strong>, qui peut réunir les
            deux ; le mode de règlement dépend de sa composition :
          </p>
          <ul className="list-disc list-inside space-y-1">
            <li>
              <strong>Panier entièrement en stock</strong> : la commande en ligne vaut <strong>réservation</strong> ;
              la vente est conclue en magasin lors du retrait, avec règlement au comptoir (article 5). Elle ne
              constitue pas une vente à distance.
            </li>
            <li>
              <strong>Panier comportant au moins un produit hors stock</strong> : la commande est validée par le
              <strong> paiement intégral, en ligne, du total du panier</strong> (article 6). Elle constitue une
              <strong> vente à distance pour l'ensemble des articles</strong> du panier, y compris ceux qui sont en
              stock. Le délai de mise à disposition court à compter du paiement.
            </li>
          </ul>
          <p>Les caractéristiques essentielles de chaque produit figurent sur sa fiche. Les photographies n'ont pas de valeur contractuelle.</p>
        </Section>

        <Section title="Article 4 — Prix">
          <p>
            Les prix sont indiqués en euros <strong>toutes taxes comprises (TTC)</strong>. Le retrait s'effectuant en
            magasin, aucun frais de livraison n'est facturé sauf mention contraire. {BUSINESS.name} se réserve le droit
            de modifier ses prix à tout moment ; le produit est facturé sur la base du tarif en vigueur au moment de la
            validation de la commande.
          </p>
        </Section>

        <Section title="Article 5 — Réservation (panier entièrement en stock)">
          <p>
            Lorsque le panier ne comporte que des produits en stock, la commande en ligne constitue une
            <strong> réservation</strong> : {BUSINESS.name} met les produits de côté. La vente n'est définitivement
            conclue qu'en magasin, lors du retrait, par le paiement du prix au comptoir.
          </p>
          <p>
            Retrait : du lundi au vendredi, de 16h30 à 18h00, sans rendez-vous, à l'adresse {BUSINESS.adresse}. La
            réservation est tenue <strong>jusqu'à 18h00 le jour même</strong> — jusqu'à 18h00 le lendemain si elle est
            enregistrée après la fermeture ; passé cette heure sans retrait, les produits sont automatiquement remis en
            vente.
          </p>
        </Section>

        <Section title="Article 6 — Commande (panier comportant au moins un produit hors stock)">
          <p>
            Dès que le panier comporte au moins un produit non détenu en stock, ce produit est commandé spécialement
            auprès du fournisseur à la demande du client et la commande est validée par le <strong>paiement intégral,
            en ligne, du total du panier</strong>, c'est-à-dire de tous les articles qu'il contient, y compris ceux qui
            sont en stock. Elle constitue alors une <strong>vente à distance</strong> pour l'ensemble de ces articles.
            Le délai de mise à disposition indiqué est estimatif et court à compter du paiement.
          </p>
          <p>
            Le paiement peut être effectué en une fois ou, selon l'éligibilité, en plusieurs fois via les facilités
            proposées par notre prestataire de paiement. Dès que la commande est prête en boutique, le client est
            prévenu par email et la retire sur place (du lundi au vendredi, 16h30-18h00, sans rendez-vous). Le client
            consommateur bénéficie du droit de rétractation prévu à l'article 8.
          </p>
          <p>
            Les articles en stock d'une telle commande ne sont retirés du catalogue qu'au moment du paiement. S'il
            apparaît à cet instant qu'un article a été vendu entre-temps et ne peut plus être fourni, {BUSINESS.name} en
            informe le client sans délai et lui rembourse le montant de cet article, au plus tard sous 14 jours ; le
            reste de la commande suit son cours, sauf demande contraire du client.
          </p>
        </Section>

        <Section title="Article 7 — Paiement">
          <p>
            Pour une <strong>commande</strong> (panier comportant au moins un produit hors stock), le
            <strong> total du panier</strong> est réglé <strong>en ligne</strong> par carte bancaire ou via PayPal
            (paiement en plusieurs fois possible selon éligibilité), via notre prestataire de paiement sécurisé ; aucun
            solde ne reste dû au retrait. Pour une <strong>réservation</strong> (panier entièrement en stock), aucun
            paiement n'est demandé en ligne : le règlement s'effectue en magasin lors du retrait. {BUSINESS.name} ne
            conserve aucune donnée de carte bancaire.
          </p>
        </Section>

        <Section title="Article 8 — Droit de rétractation">
          <p>
            Toute <strong>commande payée en ligne</strong> constitue une vente à distance. Le client consommateur
            dispose d'un délai de <strong>14 jours</strong> à compter de la réception ou du retrait du bien pour exercer
            son droit de rétractation, conformément aux articles L221-18 et suivants du Code de la consommation, sans
            avoir à motiver sa décision. Ce droit porte sur <strong>tous les articles de la commande</strong>, y compris
            ceux qui étaient en stock et qui, seuls dans le panier, auraient été réglés au comptoir. Le client en
            informe {BUSINESS.name} au moyen du formulaire type figurant en annexe, du{' '}
            <Link to="/contact" className="text-secondary hover:underline">formulaire de contact</Link> du site, ou de
            toute autre déclaration dénuée d'ambiguïté.
          </p>
          <p>
            Le client renvoie ou restitue le bien sans retard excessif et au plus tard dans les 14 jours suivant la
            communication de sa décision. Conformément à l'article L221-23 du Code de la consommation, sa responsabilité
            n'est engagée qu'à l'égard de la dépréciation du bien résultant de manipulations excédant celles qui sont
            nécessaires pour en établir la nature, les caractéristiques et le bon fonctionnement. Le remboursement
            intervient au plus tard sous 14 jours à compter de la récupération du bien ou de la preuve de son
            expédition ; les frais directs de retour restent à la charge du client.
          </p>
          <p>
            Conformément à l'article L221-28 3°, les biens nettement personnalisés (configurations assemblées sur mesure
            selon les spécifications du client) n'ouvrent pas droit à rétractation.
          </p>
          <p>
            Les <strong>réservations</strong> (panier entièrement en stock, réglé au comptoir) ne constituent pas des
            ventes à distance : la vente est conclue en magasin lors du retrait, le droit de rétractation ne s'y
            applique donc pas (achat en magasin).
          </p>
        </Section>

        <Section title="Article 9 — Garantie légale de conformité et garantie des vices cachés">
          <div className="border-2 border-slate-300 rounded-sm p-4 bg-slate-50 text-slate-700 space-y-3">
            <p>
              Le consommateur bénéficie d'un délai de <strong>2 ans</strong> à compter de la délivrance du bien pour
              obtenir la mise en œuvre de la garantie légale de conformité en cas d'apparition d'un défaut de
              conformité. Durant ce délai, le consommateur n'est tenu d'établir que l'existence du défaut de conformité
              et non la date d'apparition de celui-ci.
            </p>
            <p>
              La garantie légale de conformité emporte obligation de fournir toutes les mises à jour nécessaires au
              maintien de la conformité du bien.
            </p>
            <p>
              La garantie légale de conformité donne au consommateur droit à la réparation ou au remplacement du bien
              dans un délai de 30 jours suivant sa demande, sans frais et sans inconvénient majeur pour lui. Si le bien
              est réparé dans le cadre de la garantie légale de conformité, le consommateur bénéficie d'une extension de
              six mois de la garantie initiale. Si le consommateur demande la réparation du bien mais que le vendeur
              impose le remplacement, la garantie légale de conformité est renouvelée pour une période de deux ans à
              compter de la date de remplacement du bien.
            </p>
            <p>
              Le consommateur peut obtenir une réduction du prix d'achat en conservant le bien ou mettre fin au contrat
              en se faisant rembourser intégralement contre restitution du bien, si : 1° le professionnel refuse de
              réparer ou de remplacer le bien ; 2° la réparation ou le remplacement intervient après un délai de 30
              jours ; 3° la réparation ou le remplacement occasionne un inconvénient majeur pour le consommateur ;
              4° la non-conformité du bien persiste en dépit de la tentative de mise en conformité du vendeur restée
              infructueuse. Le consommateur a également droit à une réduction du prix ou à la résolution du contrat
              lorsque le défaut de conformité est si grave qu'il justifie que la réduction du prix ou la résolution du
              contrat soit immédiate.
            </p>
            <p>Le consommateur ne bénéficie pas du droit de résolution de la vente si le défaut de conformité est mineur.</p>
            <p>
              Toute période d'immobilisation du bien d'au moins sept jours vient s'ajouter à la durée de la garantie qui
              restait à courir. La garantie légale de conformité s'applique indépendamment de toute garantie commerciale
              éventuellement consentie.
            </p>
            <p>
              Le consommateur peut décider de mettre en œuvre la garantie contre les défauts cachés de la chose vendue
              au sens de l'article 1641 du Code civil. Dans cette hypothèse, il peut choisir entre la résolution de la
              vente ou une réduction du prix de vente, conformément à l'article 1644 du Code civil. L'action résultant
              des vices rédhibitoires doit être intentée dans un délai de deux ans à compter de la découverte du vice
              (article 1648, alinéa 1er, du Code civil).
            </p>
          </div>
          <p>
            <strong>Produits reconditionnés :</strong> la garantie légale de conformité est de 2 ans, y compris pour les
            produits reconditionnés. Pour ces produits, la présomption d'antériorité du défaut s'applique pendant 12
            mois ; au-delà, il revient au consommateur de prouver l'antériorité du défaut. La durée de la garantie reste
            de 2 ans.
          </p>
        </Section>

        <Section title="Article 10 — Garantie Atelier (garantie commerciale optionnelle)">
          <div data-testid="cgv-garantie-atelier" className="space-y-3">
            <p>
              La <strong>Garantie Atelier</strong> est une garantie commerciale payante, proposée en option lors
              de l'achat d'un ordinateur, pour une durée de <strong>1 ou 2 ans</strong> au choix du
              client, à compter de la livraison ou du retrait du bien.
            </p>
            <p>
              Elle couvre la <strong>main-d'œuvre</strong> des interventions réalisées dans notre atelier sur le
              bien couvert, sans limite de nombre. Elle porte sur ce que la garantie légale de conformité ne
              prend pas en charge : <strong>usure, panne tardive, casse, nettoyage, réinstallation</strong> — un
              défaut de conformité relève, lui, de la garantie légale de l'article 9 et reste réparé sans frais à
              ce titre. Les <strong>pièces détachées sont exclues</strong> ; elles sont facturées au prix coûtant,
              après devis présenté au client et accepté par lui. Le diagnostic et le devis sont sans frais.
            </p>
            <p>
              Sont exclus de la Garantie Atelier : la casse volontaire, tout sinistre assurable (vol, incendie,
              dégât des eaux, surtension), l'intervention sur site et la récupération de données. Le bien est
              apporté par le client à l'atelier ; aucun enlèvement ni déplacement n'est compris.
            </p>
            <p>
              Cette garantie commerciale <strong>ne réduit ni ne remplace la garantie légale de conformité</strong>
              {' '}ni la garantie légale des vices cachés mentionnées à l'article 9, qui restent dues par
              {' '}{BUSINESS.name} indépendamment d'elle.
            </p>
          </div>
        </Section>

        <Section title="Article 11 — Produits reconditionnés">
          <p>
            Conformément aux articles R122-4 et suivants du Code de la consommation, le terme « reconditionné » n'est
            employé que pour des produits ayant subi des tests portant sur l'ensemble de leurs fonctionnalités, le cas
            échéant des opérations de remise en état, et l'effacement de toutes les données de l'utilisateur précédent.
            Les mentions « état neuf », « comme neuf » ou « à neuf » ne sont pas utilisées.
          </p>
        </Section>

        <Section title="Article 12 — Disponibilité des pièces détachées">
          <p>
            Conformément à l'article L111-4 du Code de la consommation, l'information relative à la disponibilité des
            pièces détachées indispensables à l'utilisation du produit est indiquée sur la fiche produit et confirmée
            par écrit lors de l'achat.
          </p>
        </Section>

        <Section title="Article 13 — Données personnelles">
          <p>
            Les données personnelles collectées sont utilisées uniquement pour la gestion des commandes et de la
            relation client. Pour en savoir plus sur leur traitement et l'exercice de vos droits, consultez notre{' '}
            <Link to="/confidentialite" className="text-secondary hover:underline">politique de confidentialité</Link>.
          </p>
        </Section>

        <Section title="Article 14 — Médiation de la consommation">
          <p>
            Conformément aux articles L611-1 et suivants du Code de la consommation, le consommateur peut recourir
            gratuitement à un médiateur de la consommation en vue de la résolution amiable d'un litige l'opposant à
            {' '}{BUSINESS.name}, après avoir tenté de le résoudre directement par une réclamation écrite.
          </p>
          <p>
            Le consommateur peut également utiliser la plateforme européenne de règlement en ligne des litiges :{' '}
            <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">
              ec.europa.eu/consumers/odr
            </a>.
          </p>
        </Section>

        <Section title="Article 15 — Ventes aux professionnels">
          <p>
            Pour les clients professionnels (agissant dans le cadre de leur activité), les règles suivantes s'appliquent
            en lieu et place des articles 8 et 9 : le droit de rétractation des consommateurs ne s'applique pas ; la
            garantie légale de conformité du Code de la consommation ne s'applique pas. S'appliquent la garantie des
            vices cachés (articles 1641 et suivants du Code civil) et l'obligation de délivrance conforme. Les autres
            articles (prix TTC, réservation, commande et paiement, médiation hors litiges entre
            professionnels) demeurent applicables.
          </p>
        </Section>

        <Section title="Article 16 — Droit applicable">
          <p>Les présentes CGV sont soumises au droit français.</p>
        </Section>

        <Section title="Annexe — Formulaire type de rétractation">
          <p>
            Conformément à l'article R221-1 du Code de la consommation. À compléter et à renvoyer uniquement si vous
            souhaitez vous rétracter d'une commande payée en ligne (article 8).
          </p>
          <div className="border-2 border-slate-300 rounded-sm p-4 bg-slate-50 text-slate-700 space-y-3">
            <p>
              À l'attention de <strong className="text-primary">{L.raisonSociale}</strong> — {BUSINESS.adresse}, ou via
              le <Link to="/contact" className="text-secondary hover:underline">formulaire de contact</Link> du site :
            </p>
            <p>
              Je vous notifie par la présente ma rétractation du contrat portant sur la vente du bien ci-dessous :
            </p>
            <p>
              — Désignation du ou des biens : …<br />
              — Commandé le : … / reçu le : …<br />
              — Nom du (des) consommateur(s) : …<br />
              — Adresse du (des) consommateur(s) : …<br />
              — Date : …<br />
              — Signature du (des) consommateur(s) (uniquement en cas de notification du présent formulaire sur
              papier) : …
            </p>
          </div>
        </Section>

      </div>
    </div>
  );
}
